/** @jsxImportSource @emotion/react */

import { useEffect, useRef, useState } from "react";
import { InputForm } from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import { isValidEmail } from "utils/validation";
import { css } from "@emotion/react";

const enableValidBtn = (email?: string) => {
  if (email && isValidEmail(email)) return true;
  else return false;
};

const VerifyEmail = ({
  onHandleSendRequestVerifyEmail,
  onHandleVerifyCode,
}: {
  onHandleSendRequestVerifyEmail: (email: string) => Promise<{ success: boolean; message: string }>;
  onHandleVerifyCode: (email: string, code: string) => Promise<{ success: boolean; message: string }>;
}) => {
  const [email, setEmail] = useState<string>();
  const [verifyCode, setVerifyCode] = useState<string>("");
  const [successVerifyCode, setSuccessVerifyCode] = useState<boolean | null>(null);
  const [successVerifyEmail, setSuccessVerifyEmail] = useState<boolean | null>(null);
  const [emailHelpText, setEmailHelpText] = useState<string | null>(null);
  const [codeHelpText, setCodeHelpText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [time, setTime] = useState(300);
  const [startTime, setStartTime] = useState<Date | null>(null);

  const cancelTimer = () => {
    setStartTime(null);
    setTime(300);
  };

  const startTimer = () => {
    setStartTime(new Date());
    setSuccessVerifyCode(null);
    setCodeHelpText(null);
  };

  //인증 요청 버튼 클릭시
  const onClickRequestVerifyEmail = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    setVerifyCode("");
    if (email) {
      setSuccessVerifyCode(null);
      const { success, message } = await onHandleSendRequestVerifyEmail?.(email);

      setSuccessVerifyEmail(success);
      setEmailHelpText(message);
      if (success) {
        setCodeHelpText(null);
        cancelTimer();
        setTime(300);
        startTimer();
      }
    }
  };

  const onClickVerifyCode = async () => {
    if (email && verifyCode) {
      setSuccessVerifyCode(null);
      const { success, message } = await onHandleVerifyCode(email, verifyCode);
      setSuccessVerifyCode(success);
      setCodeHelpText(message);

      if (success) {
        cancelTimer();
      }
    }
  };

  const onChangeVerifyCode = (value: string) => {
    setSuccessVerifyCode(null);
    setCodeHelpText("");

    if (value.length <= 6) {
      setVerifyCode(value);
    }
  };

  useEffect(() => {
    if (startTime) {
      const interval = setInterval(() => {
        const elapsedTime = Math.floor((new Date().getTime() - startTime.getTime()) / 1000);
        const remainingTine = 300 - elapsedTime;

        if (remainingTine <= 0) {
          clearInterval(interval);
          setSuccessVerifyCode(false);
          setCodeHelpText("인증 시간이 만료되었습니다. 인증을 재요청 해주세요.");
          setTime(0);
        } else {
          setTime(remainingTine);
        }
      });
    }
  }, [startTime]);

  return (
    <InputForm>
      <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
        이메일
      </UI.Typography>
      <div
        css={css`
          display: flex;
          gap: 20px;
        `}
      >
        <UI.Input
          width={280}
          maxLength={100}
          placeholder="이메일 주소"
          size="medium"
          onChange={(value) => {
            if (value.length <= 100) setEmail(value);
            if (successVerifyEmail !== null) {
              setSuccessVerifyCode(null);
              setSuccessVerifyEmail(null);
              setEmailHelpText(null);
            }
          }}
          error={successVerifyEmail === false ? true : !email ? false : isValidEmail(email) ? false : true}
          showHelpText={successVerifyEmail === false ? true : !email ? false : isValidEmail(email) ? false : true}
          helpText={emailHelpText ?? "올바른 이메일을 입력해주세요."}
        />
        <UI.Button
          style={{ width: "100px", paddingLeft: 0, paddingRight: 0 }}
          title={successVerifyEmail ? "인증 재요청" : "인증 요청"}
          size="large"
          primary={successVerifyEmail ? false : true}
          disabled={!enableValidBtn(email) || (successVerifyCode === true ? true : false)}
          onClick={enableValidBtn(email) && !successVerifyCode ? onClickRequestVerifyEmail : () => {}}
        />
      </div>
      {successVerifyEmail && (
        <>
          <div
            css={css`
              margin-top: 12px;
              margin-bottom: 8px;
              position: relative;
            `}
          >
            <UI.Input
              disabled={successVerifyCode === true ? true : false}
              onChange={onChangeVerifyCode}
              value={verifyCode}
              placeholder="인증번호 6자리"
              showHelpText
              error={successVerifyCode === false}
              styles={
                successVerifyCode
                  ? {
                      helpText: {
                        color: "var(--alias-color-status-positive)",
                        whiteSpace: "pre-line",
                      },
                    }
                  : {
                      helpText: {
                        whiteSpace: "pre-line",
                      },
                    }
              }
              helpText={
                codeHelpText ??
                `이메일로 전송된 인증번호를 입력해주세요.\n이메일이 수신되지 않은 경우 스팸 메일함도 확인해주세요.`
              }
            />
            <UI.ButtonGroups.Text
              onClick={successVerifyCode ? () => {} : onClickVerifyCode}
              size="small"
              title="인증하기"
              disabled={verifyCode?.length === 6 ? (successVerifyCode ? true : false) : true}
              styles={{ container: { position: "absolute", top: "11px", right: "16px" } }}
            />
          </div>
          {successVerifyCode !== true && (
            <UI.Typography
              type="label01"
              fontStyle="bold"
              color={time <= 0 ? " var(--alias-color-status-negative)" : "var(--alias-color-primary-normal)"}
            >
              유효시간{" "}
              {Math.floor(time / 60)
                .toString()
                .padStart(2, "0")}
              :{(time % 60).toString().padStart(2, "0")}
            </UI.Typography>
          )}
        </>
      )}
    </InputForm>
  );
};

export default VerifyEmail;
